/**
 * Il punto in cui si scelgono le attuazioni.
 *
 * È l'unico file dell'applicazione che conosce insieme `hls.js`, `localStorage`, `fetch` e
 * i codici tasto. Tutto il resto conosce solo le porte. Se un giorno questo file si
 * duplica per Tizen, cambiano le tre righe che scelgono le attuazioni e nient'altro
 * (01-ARCHITETTURA §2.4).
 */

import { ServerClock, ProgramStore, type MonotonicSource } from '@player/domain';
import type { FocusPort, GatewayPort, PairingPort, PlayerPort, StoragePort } from '@player/ports';
import { HttpGatewayClient } from '@player/gateway-http';
import { HttpPairingClient } from '@player/pairing-http';
import { pairingConfig } from './pairingConfig.js';
import { WebStorage, ensureDeviceId } from '@player/storage-web';
import { HlsPlayer } from '@player/playback-hlsjs';
import { KeyboardFocusAdapter, detectPlatform } from '@player/focus-keyboard';
import { appVersion } from './version.js';

/**
 * Di che versione dirsi, quando qualcuno lo chiede.
 *
 * ## Qui c'era una costante scritta a mano, e mentiva
 *
 * `APP_VERSION = '0.1.0-fase1'`, ferma da mesi mentre il player era alla 0.2.1. Non era solo un
 * numero sbagliato a schermo: viaggiava **a ogni chiamata al gateway**, quindi tutte le
 * installazioni risultavano ancora alla prima versione — e nella riga del dispositivo è il numero
 * che un cliente legge all'assistenza.
 *
 * La versione vera è una sola e sta in `player-app/package.json`: la cuce Vite, la legge Gradle, ed
 * è quella con cui il rilascio si registra nel registro. Una seconda fonte scritta a mano non resta
 * allineata — questa non lo è rimasta.
 *
 * ## `dev` dove non c'è un pacchetto, e non un numero finto
 *
 * Il server di sviluppo e le prove non hanno nessun artefatto installato. Si dice `dev`, che è
 * falso per nessuno: un `0.0.0` di comodo farebbe sembrare nuova ogni versione del registro, cioè
 * proporrebbe un aggiornamento a chi è già all'ultima.
 */
export const APP_VERSION = appVersion() ?? 'dev';

const monotonic: MonotonicSource = { elapsedMs: () => performance.now() };

export interface Composition {
  readonly storage: StoragePort;
  /**
   * Dove un apparecchio nuovo scopre a chi appartiene.
   *
   * È l'unico indirizzo che il player conosce oltre al gateway, e **si compila dentro l'app**: al
   * primo avvio non c'è ancora un operatore a cui chiederlo. Indirizzo e chiave stanno nel sorgente
   * — vedi `pairingConfig.ts`, che spiega perché quella chiave può starci.
   */
  readonly pairing: PairingPort;
  readonly focusPort: FocusPort;
  readonly deviceId: string;
  readonly clock: ServerClock;
  readonly platformLabel: string;
  createGateway(): GatewayPort;
  createPlayer(video: HTMLVideoElement): PlayerPort;
}

export async function compose(): Promise<Composition> {
  const storage = new WebStorage(window.localStorage);
  const deviceId = await ensureDeviceId(storage);
  const platform = detectPlatform(navigator.userAgent);
  const focusPort = new KeyboardFocusAdapter(window, platform);
  const clock = new ServerClock(monotonic);

  return {
    storage,
    pairing: new HttpPairingClient({
      ...pairingConfig(),
      fetchImpl: window.fetch.bind(window),
    }),
    focusPort,
    deviceId,
    clock,
    platformLabel: `${platform} · ${storage.secureBacking}`,
    createGateway: () =>
      new HttpGatewayClient({
        fetchImpl: window.fetch.bind(window),
        deviceId,
        appVersion: APP_VERSION,
      }),
    createPlayer: (video) => new HlsPlayer({ video, elapsedMs: () => performance.now() }),
  };
}

/** La forma in cui `scripts/generate-fixtures.mjs` scrive la guida. */
interface EpgFixture {
  originMs: number;
  titles: string[];
  genres: string[];
  eventKinds: string[];
  channelOffsets: number[];
  startMinutes: number[];
  durationMinutes: number[];
  titleIndex: number[];
  genreIndex: number[];
  eventKindIndex: number[];
  /** Facoltative: il catalogo finto della fase 1 è stato generato prima che esistessero. */
  descriptions?: string[];
  descriptionIndex?: number[];
}

/**
 * Carica il catalogo finto della fase 1.
 *
 * Gli array arrivano già nella forma colonnare e si travasano in array tipizzati senza
 * costruire un solo oggetto `Program`: duecentosessantacinquemila oggetti creati
 * all'avvio sarebbero i quattro secondi di 00-PERIMETRO §7 spesi tutti qui.
 */
export async function loadFixtures(
  channelIds: ReadonlyArray<string>,
): Promise<ProgramStore> {
  const response = await fetch('fixtures/epg.json');
  if (!response.ok) throw new Error(`fixtures/epg.json: HTTP ${response.status}`);
  const fixture = (await response.json()) as EpgFixture;
  return new ProgramStore({
    channelOffsets: Uint32Array.from(fixture.channelOffsets),
    startMinutes: Int32Array.from(fixture.startMinutes),
    durationMinutes: Uint16Array.from(fixture.durationMinutes),
    titleIndex: Uint32Array.from(fixture.titleIndex),
    genreIndex: Uint16Array.from(fixture.genreIndex),
    eventKindIndex: Uint8Array.from(fixture.eventKindIndex),
    /*
      Il catalogo finto può non avere le descrizioni: è stato generato prima che il magazzino le
      tenesse. In quel caso l'indice è tutto a zero e il serbatoio contiene la stringa vuota — che è
      **esattamente** quel che c'era prima, cioè nessuna descrizione, e non una riga inventata.
    */
    descriptionIndex: Uint32Array.from(
      fixture.descriptionIndex ?? new Array<number>(fixture.startMinutes.length).fill(0),
    ),
    descriptions: fixture.descriptions ?? [''],
    titles: fixture.titles,
    genres: fixture.genres,
    eventKinds: fixture.eventKinds,
    originMs: fixture.originMs,
    channelIds,
  });
}

export async function loadFixtureChannels(): Promise<ReadonlyArray<unknown>> {
  const response = await fetch('fixtures/channels.json');
  if (!response.ok) throw new Error(`fixtures/channels.json: HTTP ${response.status}`);
  return (await response.json()) as ReadonlyArray<unknown>;
}
